import { gsap } from "gsap";
import * as THREE from "three";

import {
  generateParticleModel,
  isParticleModelName,
} from "./particleModels.js";
import {
  particleFragmentShader,
  particleVertexShader,
} from "./shader.js";
import { clamp, createSeededRandom } from "./utils.js";

const MIN_PARTICLES = 50_000;
const MAX_PARTICLES = 150_000;
const DEFAULT_PARTICLES = 100_000;

/**
 * High-density GPU particle system with double-buffered model morphing.
 *
 * The CPU only generates target buffers when a model is requested. Every
 * transition frame, including scatter and attraction, is evaluated in GLSL.
 */
export class ParticleSystem {
  constructor(
    scene,
    {
      count = DEFAULT_PARTICLES,
      maxCount = MAX_PARTICLES,
      seed = 0x51a7f13d,
      initialModel = "saturn",
    } = {},
  ) {
    if (!isParticleModelName(initialModel)) {
      throw new RangeError("Unknown initial particle model: " + initialModel);
    }

    this.scene = scene;
    this.seed = seed;
    this.maxCount = clamp(Math.round(maxCount), MIN_PARTICLES, MAX_PARTICLES);
    this.count = clamp(Math.round(count), MIN_PARTICLES, this.maxCount);
    this.currentModel = initialModel;
    this.targetModel = null;
    this.isMorphing = false;
    this.reducedMotion = false;
    this.pendingTransition = null;
    this.activeTransition = null;
    this.resolveActiveTransition = null;
    this.paletteTweens = [];
    this.isDisposed = false;
    this.rotationOffset = { x: 0, y: 0 };

    const initial = generateParticleModel(
      initialModel,
      this.maxCount,
      this.seed,
    );
    const randoms = createParticleRandoms(this.maxCount, this.seed ^ 0xa3c59ac3);

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(initial.positions, 3),
    );
    this.geometry.setAttribute(
      "aTarget",
      new THREE.BufferAttribute(initial.positions.slice(), 3),
    );
    this.geometry.setAttribute(
      "aRandom",
      new THREE.BufferAttribute(randoms, 4),
    );
    this.geometry.setAttribute(
      "aSize",
      new THREE.BufferAttribute(initial.sizeWeights, 1),
    );
    this.geometry.setAttribute(
      "aTargetSize",
      new THREE.BufferAttribute(initial.sizeWeights.slice(), 1),
    );
    this.geometry.setAttribute(
      "aColorMix",
      new THREE.BufferAttribute(initial.colorWeights, 1),
    );
    this.geometry.setAttribute(
      "aTargetColorMix",
      new THREE.BufferAttribute(initial.colorWeights.slice(), 1),
    );
    this.geometry.setDrawRange(0, this.count);
    this.geometry.boundingSphere = new THREE.Sphere(
      new THREE.Vector3(),
      7.5,
    );

    this.uniforms = {
      uTime: { value: 0 },
      uPixelRatio: { value: 1 },
      uPointSize: { value: 1.4 },
      uTurbulence: { value: 0.76 },
      uBreath: { value: 1 },
      uMotionScale: { value: 1 },
      uMorphProgress: { value: 0 },
      uScatterPower: { value: 1.75 },
      uExpansion: { value: 0.5 },
      uAttraction: { value: 0 },
      uAttractor: { value: new THREE.Vector3() },
      uReleaseImpulse: { value: 0 },
      uOpacity: { value: 1 },
      uGlow: { value: 1.07 },
      uColorA: { value: new THREE.Color(initial.palette.primary) },
      uColorB: { value: new THREE.Color(initial.palette.secondary) },
      uFogColor: { value: new THREE.Color(0x040612) },
      uFogDensity: { value: 0.055 },
    };

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: particleVertexShader,
      fragmentShader: particleFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthTest: true,
      depthWrite: false,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.name = "MorphingParticleField";
    this.points.frustumCulled = false;
    this.points.renderOrder = 1;
    this.points.rotation.set(-0.055, 0.08, -0.035);
    this.scene.add(this.points);
  }

  setDensity(value) {
    const nextCount =
      value <= 1
        ? MIN_PARTICLES + value * (this.maxCount - MIN_PARTICLES)
        : value;

    this.count = clamp(
      Math.round(nextCount / 1000) * 1000,
      MIN_PARTICLES,
      this.maxCount,
    );
    this.geometry.setDrawRange(0, this.count);
  }

  setParticleSize(value) {
    this.uniforms.uPointSize.value = clamp(value, 0.4, 4);
  }

  setGlow(value) {
    this.uniforms.uGlow.value = clamp(0.72 + value * 0.28, 0.72, 1.56);
  }

  setPixelRatio(value) {
    this.uniforms.uPixelRatio.value = clamp(value, 1, 2);
  }

  setColorMode(colorA, colorB = colorA) {
    this.killPaletteTweens();
    this.uniforms.uColorA.value.set(colorA);
    this.uniforms.uColorB.value.set(colorB);
  }

  setReducedMotion(enabled) {
    this.reducedMotion = enabled;
    this.uniforms.uMotionScale.value = enabled ? 0.18 : 1;
    this.uniforms.uScatterPower.value = enabled ? 0.45 : 1.75;
  }

  setInteraction({
    expansion = 0.5,
    attraction = 0,
    attractor = { x: 0, y: 0, z: 0 },
    releaseImpulse = 0,
  } = {}) {
    this.uniforms.uExpansion.value = clamp(expansion, 0, 1);
    this.uniforms.uAttraction.value = clamp(attraction, 0, 1);
    this.uniforms.uAttractor.value.set(
      clamp(attractor.x, -4.5, 4.5),
      clamp(attractor.y, -3.5, 3.5),
      clamp(attractor.z, -2.5, 2.5),
    );
    this.uniforms.uReleaseImpulse.value = clamp(releaseImpulse, 0, 1);
  }

  setRotation(x, y) {
    this.rotationOffset.x = clamp(x, -1.4, 1.4);
    this.rotationOffset.y = y;
  }

  /**
   * Requests a model transition. If a morph is already running, only the most
   * recent pending request is retained.
   */
  setModel(name, options = {}) {
    if (!isParticleModelName(name)) {
      return Promise.reject(new RangeError("Unknown particle model: " + name));
    }
    if (this.isDisposed) {
      return Promise.resolve(false);
    }
    if (!this.isMorphing && name === this.currentModel) {
      return Promise.resolve(false);
    }
    if (this.isMorphing && name === this.targetModel) {
      return this.activeTransition;
    }

    if (this.isMorphing) {
      if (this.pendingTransition?.name === name) {
        return this.pendingTransition.promise;
      }
      this.pendingTransition?.resolve(false);

      const pending = { name, options };
      pending.promise = new Promise((resolve, reject) => {
        pending.resolve = resolve;
        pending.reject = reject;
      });
      this.pendingTransition = pending;
      return pending.promise;
    }

    return this.startModelTransition(name, options);
  }

  startModelTransition(name, options = {}) {
    let target;
    try {
      target = generateParticleModel(name, this.maxCount, this.seed);
      this.uploadTarget(target);
    } catch (error) {
      return Promise.reject(error);
    }

    this.isMorphing = true;
    this.targetModel = name;
    this.uniforms.uMorphProgress.value = 0;
    this.animatePalette(target.palette, options.duration);

    const duration =
      options.duration ?? (this.reducedMotion ? 0.9 : 2.45);

    this.activeTransition = new Promise((resolve) => {
      this.resolveActiveTransition = resolve;
      this.morphTween = gsap.to(this.uniforms.uMorphProgress, {
        value: 1,
        duration,
        ease: "none",
        onComplete: () => {
          this.commitTarget();
          this.currentModel = name;
          this.targetModel = null;
          this.isMorphing = false;
          this.uniforms.uMorphProgress.value = 0;
          this.morphTween = null;
          this.resolveActiveTransition = null;
          this.activeTransition = null;
          resolve(true);
          this.runPendingTransition();
        },
      });
    });

    return this.activeTransition;
  }

  uploadTarget(target) {
    const targetPosition = this.geometry.getAttribute("aTarget");
    const targetSize = this.geometry.getAttribute("aTargetSize");
    const targetColor = this.geometry.getAttribute("aTargetColorMix");

    targetPosition.array.set(target.positions);
    targetSize.array.set(target.sizeWeights);
    targetColor.array.set(target.colorWeights);

    targetPosition.needsUpdate = true;
    targetSize.needsUpdate = true;
    targetColor.needsUpdate = true;
  }

  commitTarget() {
    const pairs = [
      ["position", "aTarget"],
      ["aSize", "aTargetSize"],
      ["aColorMix", "aTargetColorMix"],
    ];

    pairs.forEach(([currentName, targetName]) => {
      const current = this.geometry.getAttribute(currentName);
      const target = this.geometry.getAttribute(targetName);
      this.geometry.setAttribute(currentName, target);
      this.geometry.setAttribute(targetName, current);
    });
  }

  runPendingTransition() {
    const pending = this.pendingTransition;
    this.pendingTransition = null;

    if (!pending) {
      return;
    }
    if (pending.name === this.currentModel) {
      pending.resolve(false);
      return;
    }

    this.startModelTransition(pending.name, pending.options).then(
      pending.resolve,
      pending.reject,
    );
  }

  animatePalette(palette, requestedDuration) {
    this.killPaletteTweens();
    const colorA = new THREE.Color(palette.primary);
    const colorB = new THREE.Color(palette.secondary);
    const duration =
      (requestedDuration ?? (this.reducedMotion ? 0.9 : 2.45)) * 0.78;

    this.paletteTweens = [
      gsap.to(this.uniforms.uColorA.value, {
        r: colorA.r,
        g: colorA.g,
        b: colorA.b,
        duration,
        ease: "power2.inOut",
      }),
      gsap.to(this.uniforms.uColorB.value, {
        r: colorB.r,
        g: colorB.g,
        b: colorB.b,
        duration,
        ease: "power2.inOut",
      }),
    ];
  }

  killPaletteTweens() {
    this.paletteTweens.forEach((tween) => tween.kill());
    this.paletteTweens.length = 0;
  }

  update(time, delta) {
    this.uniforms.uTime.value = time;
    const motionScale = this.uniforms.uMotionScale.value;
    this.points.rotation.x =
      -0.055 +
      this.rotationOffset.x +
      Math.sin(time * 0.07) * 0.025 * motionScale;
    this.points.rotation.y =
      0.08 +
      this.rotationOffset.y +
      Math.sin(time * 0.09) * 0.065 * motionScale;
    this.points.rotation.z = -0.035 + Math.sin(time * 0.05) * 0.018 * motionScale;
  }

  dispose() {
    this.isDisposed = true;
    this.morphTween?.kill();
    this.resolveActiveTransition?.(false);
    this.pendingTransition?.resolve(false);
    this.pendingTransition = null;
    this.killPaletteTweens();
    this.points.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}

function createParticleRandoms(count, seed) {
  const random = createSeededRandom(seed);
  const values = new Float32Array(count * 4);
  for (let index = 0; index < values.length; index += 1) {
    values[index] = random();
  }
  return values;
}
